import type {
  ChecklistCategory,
  ExpenseCategory,
  ItemKind,
  ItemStatus,
  MemberRole,
  PlaceCategory,
  PlaceStatus,
  TransportMode,
} from "@shared/types";
import { t } from "@/i18n";

export const CATEGORY: Record<PlaceCategory, { label: string; emoji: string; color: string }> = {
  city: { label: t("Город / база"), emoji: "🏙️", color: "#0f766e" },
  sight: { label: t("Достопримечательность"), emoji: "🏛️", color: "#7c3aed" },
  nature: { label: t("Природа"), emoji: "⛰️", color: "#15803d" },
  beach: { label: t("Пляж"), emoji: "🏖️", color: "#0284c7" },
  food: { label: t("Еда"), emoji: "🍜", color: "#e2483d" },
  cafe: { label: t("Кафе / кофе"), emoji: "☕", color: "#a16207" },
  nightlife: { label: t("Бары / ночь"), emoji: "🍸", color: "#db2777" },
  hotel: { label: t("Жильё"), emoji: "🛏️", color: "#4f46e5" },
  activity: { label: t("Активность"), emoji: "🎯", color: "#ea580c" },
  shopping: { label: t("Рынок / шопинг"), emoji: "🛍️", color: "#c026d3" },
  transport: { label: t("Транспорт"), emoji: "✈️", color: "#475569" },
  other: { label: t("Другое"), emoji: "📍", color: "#64748b" },
};

export const PLACE_STATUS: Record<PlaceStatus, { label: string; short: string }> = {
  idea: { label: t("Идея"), short: t("идея") },
  want: { label: t("Хотим"), short: t("хотим") },
  visited: { label: t("Были"), short: t("были") },
  skipped: { label: t("Отказались"), short: t("нет") },
};

export const ITEM_KIND: Record<ItemKind, { label: string; emoji: string }> = {
  visit: { label: t("Посещение"), emoji: "📍" },
  activity: { label: t("Активность"), emoji: "🎯" },
  stay: { label: t("Ночёвка"), emoji: "🛏️" },
  transport: { label: t("Переезд"), emoji: "🚌" },
  note: { label: t("Заметка"), emoji: "📝" },
};

export const ITEM_STATUS: Record<ItemStatus, { label: string; tone: string }> = {
  idea: { label: t("черновик"), tone: "bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300" },
  planned: { label: t("в плане"), tone: "bg-jade-100 text-jade-800 dark:bg-jade-400/15 dark:text-jade-200" },
  booked: { label: t("забронировано"), tone: "bg-indigo-100 text-indigo-800 dark:bg-indigo-400/15 dark:text-indigo-200" },
  done: { label: t("сделано"), tone: "bg-slate-200 text-slate-700 dark:bg-white/10 dark:text-slate-300" },
  cancelled: { label: t("отменено"), tone: "bg-rose-100 text-rose-700 line-through dark:bg-rose-400/15 dark:text-rose-300" },
};

export const MODE: Record<TransportMode, { label: string; emoji: string; dash?: string }> = {
  flight: { label: t("Самолёт"), emoji: "✈️", dash: "2 8" },
  train: { label: t("Поезд"), emoji: "🚆" },
  sleeper_bus: { label: t("Ночной автобус"), emoji: "🌙", dash: "1 6" },
  bus: { label: t("Автобус"), emoji: "🚌" },
  car: { label: t("Машина / трансфер"), emoji: "🚗" },
  motorbike: { label: t("Мотобайк"), emoji: "🏍️" },
  boat: { label: t("Лодка / паром"), emoji: "⛴️", dash: "6 6" },
  taxi: { label: t("Такси / Grab"), emoji: "🚕" },
  walk: { label: t("Пешком"), emoji: "🚶", dash: "1 5" },
  other: { label: t("Другое"), emoji: "➡️" },
};

export const ROLE: Record<MemberRole, string> = { owner: t("владелец"), editor: t("редактор"), viewer: t("только просмотр") };

export const CHECK_CAT: Record<ChecklistCategory, { label: string; emoji: string }> = {
  documents: { label: t("Документы"), emoji: "🛂" },
  booking: { label: t("Брони"), emoji: "🎫" },
  money: { label: t("Деньги"), emoji: "💳" },
  health: { label: t("Здоровье и страховка"), emoji: "🩺" },
  packing: { label: t("Вещи"), emoji: "🎒" },
  tech: { label: t("Связь и приложения"), emoji: "📱" },
  other: { label: t("Другое"), emoji: "📌" },
};

export const EXPENSE_CAT: Record<ExpenseCategory, { label: string; emoji: string; color: string }> = {
  transport: { label: t("Транспорт"), emoji: "✈️", color: "#0284c7" },
  stay: { label: t("Жильё"), emoji: "🛏️", color: "#4f46e5" },
  food: { label: t("Еда"), emoji: "🍜", color: "#e2483d" },
  activities: { label: t("Активности"), emoji: "🎯", color: "#ea580c" },
  shopping: { label: t("Покупки"), emoji: "🛍️", color: "#c026d3" },
  fees: { label: t("Визы и сборы"), emoji: "🧾", color: "#64748b" },
  other: { label: t("Другое"), emoji: "📌", color: "#94a3b8" },
};

/** Цвет дня: мягкая радуга вдоль маршрута */
export function dayColor(index: number, total: number) {
  const t = total <= 1 ? 0 : index / (total - 1);
  const hue = (168 + t * 250) % 360;
  return `hsl(${hue.toFixed(0)} 72% 42%)`;
}
